import { useEffect, useState } from 'react'
import { Box, CircularProgress, Grid, type SelectChangeEvent, Tab, Tabs, Typography } from '@mui/material'
import { IsdDocumentTypeStatus, type postError, type isdProgram, type ageSetting, type fplSettings } from 'core/types'
import { Modal } from 'Components/Modal'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { toast } from 'react-toastify'
import { ProgramAgeSetting } from 'Pages/ISDSettings/ProgramAgeSetting'
import { TabDisplay } from 'Components/TabDisplay'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import ReduceCapacityIcon from '@mui/icons-material/ReduceCapacity'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import FeedOutlinedIcon from '@mui/icons-material/FeedOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
// import { ContinuousEnrollmentIcon } from 'Assets/SVGs/ContinuousEnrollment'
import { SwitchWithToolTip } from 'Components/Switch/SwitchWithToolTip'
import 'Pages/ISDSettings/ProgramSettings.css'
import S3Image from 'Components/S3Image'
import { OnOffSwitch } from 'Components/Switch/OnOffSwitch'
import { TextFieldWithLabel } from 'Components/TextField'
import { LabelWithToolTip } from 'Components/Label/Index'
import { CustomLabelWithToolTip } from 'Components/CustomLabel/Index'
import { useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import AlertMessage from 'Components/AlertMessage'
export interface ProgramSettingProps {
  open: boolean
  onClose: () => void
  isdID: number
  program: isdProgram
  errors?: string[] | undefined
  priorityId: number | null
}
function IncomeSettingDisplay (props: { val1: string, val2: string, fontWeight?: string }): JSX.Element {
  return <Grid item><Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
    <Typography width='50%' fontWeight={props.fontWeight}>{props.val1}</Typography>
    <Typography width='50%' fontWeight={props.fontWeight}>{props.val2}</Typography>
  </Box></Grid>
}
function DocumentDisplay (props: { fileType: fileType, docsList: Array<{ id: number, status: string }>, onChange: (checked: boolean, id: number,) => void }): JSX.Element {
  const thisDoc = props.docsList.filter(d => d.id === props.fileType.id)[0] ?? { id: props.fileType.id, status: IsdDocumentTypeStatus.NOTREQUIRED }
  return <Grid item>
    <Box sx={{ border: '1px solid black', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <S3Image style={{ height: '50px', width: '50px' }} imageKey={props.fileType.s3Key} />
        <Box>
          <Typography>{props.fileType.name}</Typography>
          <Typography>{props.fileType.description}</Typography>
        </Box>
      </Box>
      <OnOffSwitch checked={thisDoc.status === IsdDocumentTypeStatus.REQUIRED}
        onChange={(evt: any, checked: boolean) => { props.onChange(checked, props.fileType.id) }}
      />
    </Box>
  </Grid>
}
interface fileType {
  id: number
  name: string
  description: string
  s3Key: string
  enabled: boolean
}

function SettingDisplay (props: {
  name: string
  text: string
  toolTip: string | undefined
  icon: JSX.Element
  checked: boolean
  disabled?: boolean
  onChange: (evt: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
}): JSX.Element {
  return <Grid item sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {props.icon}
      <Typography>
        {props.text}
      </Typography>
    </Box>
    <SwitchWithToolTip disabled={props.disabled}
      checked={props.checked ?? false} onChange={props.onChange } name={props.name}
      dataTestId='enabledSwitch' toolTip={props.toolTip}
    />
  </Grid>
}
function HeaderDisplay (props: { name: string, icon: JSX.Element, checked?: boolean, showCheck: boolean, checkDisabled?: boolean, onCheckChange?: (val: boolean) => void }): JSX.Element {
  return <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
    <Box sx={{ display: 'flex', gap: 2 }}>
      {props.icon}
      <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>{props.name}</Typography>
    </Box>
    {props.showCheck && <OnOffSwitch checked={props.checked} onChange={(evt: any, val: boolean) => { props.onCheckChange?.(val) }} disabled={props.checkDisabled ?? false} />}
  </Box>
}
function SettingsGroup (props: { name: string, icon: JSX.Element, children: JSX.Element, checked?: boolean, checkDisabled?: boolean, showCheck: boolean, onCheckChange?: (val: boolean) => void }): JSX.Element {
  return <Box sx={{ border: '1px solid black', borderRadius: '8px', padding: '20px' }}>
    <HeaderDisplay {...props} />
    {props.children}
  </Box>
}
export function ProgramSettings (props: ProgramSettingProps): JSX.Element {
  const [isdProgramData, setISDProgramData] = useState<isdProgram>()
  const [displayModal, setDisplayModal] = useState(false)
  const [fileTypes, setFileTypes] = useState<Array<{ id: number, name: string, description: string, s3Key: string, enabled: boolean, incomeRelated: boolean }>>([])
  const [selectedFileTypeIDs, setSelectedFileTypeIDs] = useState<Array<{ id: number, status: string }>>([])
  const [fplSettings, setFplSettings] = useState<fplSettings>({ lowAge: 0, highAge: 4, fplIncomeThreshhold: 0 })
  const [loaded, setLoaded] = useState(false)
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }
  const submitDisable = async (data: isdProgram | null): Promise<void> => {
    const { success } = await sendPost('/ISDPrograms/ToggleProgram', {
      ID: data?.id,
      ISDID: data?.isdid,
      ProgramID: data?.programID,
      enabled: false
    })

    if (!success) {
      toast.error('There was an error saving the change.')
    } else {
      refresh()
      props.onClose()
    }
    setDisplayModal(false)
  }

  const [prioPrograms, setPrioPrograms] = useState<isdProgram[]>([])

  const [tabIndex, setTabIndex] = useState(0)
  const prioritizedProgram = prioPrograms.filter(p => p.id === isdProgramData?.prioritizedProgramID)[0]

  useEffect(() => {
    const loadFormData = async (): Promise<void> => {
      const { response, error } = await sendGet(`/ISDDocument/GetIsdDocuments?isdid=${props.isdID}`, {})
      if (error[0] === '') {
        setFileTypes(response)
      }

      const { response: rsp2, error: error2 } = await sendGet(`/ISDPrograms/GetISDProgramDocument?ISDID=${props.isdID}&programID=${props.program.id}`, {})
      if (error2[0] === '') {
        const list: Array<{ id: number, status: string }> =
          rsp2.map((x: { documentID: number, status: string }) => {
            return { id: x.documentID, status: x.status }
          })
        setSelectedFileTypeIDs(list)
      }

      const { response: rsp3, error: error3 } = await sendGet(`/ISDPrograms/GetByProgram?isdID=${props.isdID}&programID=${props.program.id}`, {})
      if (error3[0] === '') {
        setISDProgramData(rsp3)
        const ageSettings: ageSetting[] = rsp3.ageSettingList
        const sorted = ageSettings.sort((a, b) => a.allowForAge - b.allowForAge)
        const firstElement = sorted.shift()
        if (firstElement === null) {
          setFplSettings({ fplIncomeThreshhold: 100, highAge: 4, lowAge: 0 })
        }
        let lastElement = sorted.pop()
        if (lastElement == null) { lastElement = firstElement }
        const newFplSetting: fplSettings = {
          fplIncomeThreshhold: lastElement?.fplIncomeThreshhold ?? 0,
          highAge: lastElement?.allowForAge ?? 0,
          lowAge: firstElement?.allowForAge ?? 0
        }
        setFplSettings(newFplSetting)
      }
      const { response: rsp4, error: error4 } = await sendGet(`/ISDPrograms/GetPriorityPrograms?isdID=${props.isdID}`, {})
      if (error4[0] === '') {
        setPrioPrograms(rsp4)
      }

      setLoaded(true)
    }
    void loadFormData()
  }, [])

  const handleClose = (): void => {
    props.onClose()
  }
  const handleChange = (checked: boolean, documentId: number): void => {
    const tempIds = selectedFileTypeIDs.slice()
    if (selectedFileTypeIDs !== undefined) {
      const selecteddocument = tempIds.find(x => x.id === documentId)
      if (selecteddocument !== undefined) {
        selecteddocument.status = checked ? IsdDocumentTypeStatus.REQUIRED : IsdDocumentTypeStatus.NOTREQUIRED
      } else {
        const doc = fileTypes.find(x => x.id === documentId)
        if (doc !== undefined) {
          tempIds.push({ id: doc.id, status: IsdDocumentTypeStatus.NOTREQUIRED })
        }
      }

      setSelectedFileTypeIDs(tempIds)
    }
  }
  const handleInputChange = (event: SelectChangeEvent<number>): void => {
    if (isdProgramData != null
    ) {
      const newVal = Number.parseInt(event.target.value as string)
      setISDProgramData({ ...isdProgramData, incomeVerificiationModeID: newVal })
    }
  }
  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    if (isdProgramData != null) {
      setISDProgramData({ ...isdProgramData, [event.target.name]: checked })
    }
  }
  const handleWaiverCheckChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    if (isdProgramData != null) {
      setISDProgramData({ ...isdProgramData, autoWaiverCreateForEligibleChildren: checked && isdProgramData?.priorityProgram })
    }
  }
  const prioProgramChange = (checked: boolean): void => {
    if (isdProgramData != null && !isdProgramData.priorityProgram) {
      setISDProgramData({ ...isdProgramData, prioritizeOtherProgram: checked, prioritizedProgramID: checked ? isdProgramData.prioritizedProgramID : null })
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    } else if (isdProgramData != null && isdProgramData.priorityProgram) {
      setISDProgramData({ ...isdProgramData, prioritizeOtherProgram: false, prioritizedProgramID: null })
    }
  }
  const manualVerificationChange = (checked: boolean): void => {
    if (isdProgramData != null) {
      setISDProgramData({ ...isdProgramData, requiresManualVerification: checked })
    }
  }
  const changePrioProgramID = (event: SelectChangeEvent<number>): void => {
    if (isdProgramData != null) {
      const newVal = Number.parseInt(event.target.value as string)
      setISDProgramData({ ...isdProgramData, prioritizedProgramID: newVal })
    }
  }

  const handlePrioCheckChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    if (isdProgramData != null) {
      setISDProgramData({
        ...isdProgramData,
        [event.target.name]: checked,
        autoWaiverCreateForEligibleChildren: checked && isdProgramData?.autoWaiverCreateForEligibleChildren,
        prioritizeOtherProgram: !checked && isdProgramData.prioritizeOtherProgram,
        prioritizedProgramID: !checked ? isdProgramData.prioritizedProgramID : null

      })
    }
  }

  const handleTextChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (isdProgramData !== undefined) {
      setISDProgramData({ ...isdProgramData, [e.target.name]: e.target.value })
    }
  }

  const fplChanged = (ageSetting: fplSettings): void => {
    const newSettings: fplSettings = { ...fplSettings, fplIncomeThreshhold: ageSetting.fplIncomeThreshhold }
    setFplSettings(newSettings)
  }
  const lowAgeChange = (ageSetting: SelectChangeEvent<number>): void => {
    const num = Number.parseInt(ageSetting.target.value.toString())
    const newSettings: fplSettings = { ...fplSettings, lowAge: num }
    setFplSettings(newSettings)
  }
  const highAgeChange = (ageSetting: SelectChangeEvent<number>): void => {
    const num = Number.parseInt(ageSetting.target.value.toString())

    const newSettings: fplSettings = { ...fplSettings, highAge: num }
    setFplSettings(newSettings)
  }

  const handleSubmit = async (): Promise<void> => {
    if (isdProgramData == null) {
      return
    }

    await submit()
  }

  const submit = async (): Promise<void> => {
    const errors: string[] = []
    if (isdProgramData == null) {
      return
    }

    if (isdProgramData.prioritizeOtherProgram && isdProgramData.prioritizedProgramID == null) {
      errors.push('Please select a program to prioritize')
    }
    if (isdProgramData.requiresIncomeCalculation) {
      const files = fileTypes.filter(i => i.incomeRelated)
      const enabledFiles = selectedFileTypeIDs.some(f => files.some(fi => fi.id === f.id) && f.status === IsdDocumentTypeStatus.REQUIRED)
      if (!enabledFiles) { errors.push('Please select at least one income document to be required.') }

      if (isdProgramData.incomeVerificiationModeID == null) { errors.push('Please select a verification lifetime.') }
    }
    if (errors.length > 0) {
      errors.forEach(e => toast.error(e))
      return
    }
    const { success } = await sendPost('/ISDPrograms/UpdateISDDocumentTypes', {
      isdID: props.isdID,
      isdProgramID: props.program.id,
      fileTypeIDs: selectedFileTypeIDs
    })
    if (success) {
      const mappedAgeSettings: ageSetting[] = []
      const min = Math.min(fplSettings.lowAge, fplSettings.highAge)
      const max = Math.max(fplSettings.lowAge, fplSettings.highAge)
      const count = max - min + 1
      let index = 0
      let current = min
      while (index < count) {
        const val: ageSetting = {
          allowForAge: current,
          fplIncomeThreshhold: fplSettings.fplIncomeThreshhold,
          id: 0,
          maxEnrollmentAllocationPercent: 0,
          requiresApproval: true
        }
        mappedAgeSettings.push(val)
        index++
        current++
      }

      isdProgramData.ageSettingList = mappedAgeSettings
      isdProgramData.programID = props.program.programID
      const { response } = await sendPost('/ISDPrograms/Update', isdProgramData)
      const resp: { success: boolean, errors: postError[], newRecordID: number } = response
      if (resp.success) {
        props.onClose()
        refresh()
        toast.success('Program settings saved')
      } else {
        resp.errors.filter(e => e.field == null).forEach(e => {
          toast.error(e.error)
        })
      }
    }
  }
  const handleEnableChange = (val: boolean): void => {
    if (isdProgramData != null) {
      if (val) { setISDProgramData({ ...isdProgramData, enabled: val }) } else {
        setDisplayModal(true)
      }
    }
  }

  const titleContent = <div className="d-flex f-align-items-center">
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <span>Program Settings - {props.program.name}</span>
      <Tabs value={tabIndex} onChange={(e, val) => { setTabIndex(val) }} data-testid="tabsHeading">
        <Tab sx={{ textTransform: 'none' }} label="General" value={0} data-testid='general' />
        <Tab sx={{ textTransform: 'none' }} label="Verification" value={1} data-testid='verification' />
      </Tabs>
    </Box>
  </div>
  const selectOptions = Array<{ id: number, name: string }>({ id: 0, name: '0' }, { id: 1, name: '1' }, { id: 2, name: '2' }, { id: 3, name: '3' }, { id: 4, name: '4+' })

  if (!loaded) {
    return <CircularProgress role='loading' />
  } else {
    const content = <>
      <Modal
        onClose={() => { setDisplayModal(false) }}
        onConfirm={() => {
          if (isdProgramData != null) { void submitDisable(isdProgramData) }
        }}
        open={displayModal}
        confirmationContent={<>Deactivating this program will disable it from being used for future enrollments. Any enrollments or referrals currently in process will be allowed to complete. Do you wish to continue?</>}
      />
      {((isdProgramData?.requiresIncomeCalculation) ?? false) && (
        <AlertMessage icon={<ErrorOutlineOutlinedIcon />} message='This Program is income bound.  Additional settings must be set before it can be saved.' type='warning' dismissible={false} />
      )}
      <TabDisplay index={0} value={tabIndex} >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: '10px' }}>
          <SettingsGroup name='Program Settings' icon={<SettingsOutlinedIcon sx={{ width: '30px', height: '30px' }} />} showCheck={false}>
            <Grid container sx={{ pl: '20px' }}>
              <SettingDisplay checked={isdProgramData?.enabled ?? false} icon={<CheckIcon />} name="enabled" text='Enable Program'
                toolTip='This will allow this program to be configured for service providers during enrollments.'
                onChange={(evt, val) => { handleEnableChange(val) }}
              />
              <SettingDisplay checked={isdProgramData?.allowConcurrentEnrollment ?? false} icon={<KeyboardDoubleArrowRightIcon />}
                name="allowConcurrentEnrollment" text='Concurrent Enrollment' onChange={handleCheckChange}
                toolTip='This setting will allow more than one enrollment to exist within the same school year for this a student.  Leaving this setting off will enforce concurrent enrollment checks when creating a new enrollment.'
              />
              <SettingDisplay checked={isdProgramData?.noCapacityLimit ?? false} icon={<ReduceCapacityIcon />}
                name="noCapacityLimit" text='No Capacity Limit' onChange={handleCheckChange}
                toolTip='This setting will disable enforcement of capacity rules during the enrollment process.'
              />
              <SettingDisplay checked={isdProgramData?.priorityProgram ?? false} disabled={!(props.priorityId == null || props.priorityId === isdProgramData?.id)} icon={<PriorityHighIcon />}
                name="priorityProgram" text='Allow to be priority for other programs' onChange={handlePrioCheckChange}
                toolTip='This setting will allow this program to set another program as their priority, meaning that program will need to follow the rules of this program to complete an enrollment.  It could result in a waiver being required for the associated income verification.'
              />
              {(isdProgramData?.priorityProgram ?? false) && (
                <Box sx={{ pl: '20px', width: '100%' }}>
                  <SettingDisplay checked={isdProgramData?.autoWaiverCreateForEligibleChildren ?? false} icon={<BoltRoundedIcon />}
                    name="autoWaiverCreateForEligibleChildren" text='Auto Waiver Eligible Children' onChange={handleWaiverCheckChange}
                    toolTip='Any child that is marked as homeless or under foster care will automatically have a waiver created for any program that prioritizes this program.'
                  />
                </Box>
              )}
              <Grid item width={'100%'}>
                <TextFieldWithLabel
                  className='TextArea'
                  maxWidth='100%'
                  label='Description'
                  name='description'
                  value={isdProgramData?.description ?? ''}
                  onChange={handleTextChange}
                  toolTip='The description should describe the program and a brief summary of the requirements.  It will be shown in various places in the application.'
                  dataTestId='description'
                />
              </Grid>
            </Grid>
          </SettingsGroup>
        </Box>

      </TabDisplay>
      <TabDisplay index={1} value={tabIndex}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: '10px' }}>
          <SettingsGroup name={'Manual Verification'} showCheck={true} checked={((isdProgramData?.requiresManualVerification) ?? false) || ((isdProgramData?.requiresIncomeCalculation) ?? false)}
            onCheckChange={manualVerificationChange} checkDisabled={isdProgramData?.requiresIncomeCalculation}
            icon={<></>}
          >
            <>
              {(((isdProgramData?.requiresManualVerification) ?? false) || ((isdProgramData?.requiresIncomeCalculation) ?? false)) && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box sx={{ width: '50%' }}>
                      <LabelWithToolTip labelText='Allow For Age Range' />
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <SelectWithLabel
                          options={selectOptions}
                          value={fplSettings.lowAge}
                          onChange={lowAgeChange} name={''}
                        />
                        <Typography>to</Typography>
                        <SelectWithLabel
                          options={selectOptions}
                          value={fplSettings.highAge}
                          onChange={highAgeChange} name={''}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ width: '50%' }}>
                      <LabelWithToolTip labelText='Verification Lifetime'
                        toolTipText={`This setting will determine how long an income verification is valid for. 
                  Selecting School Year means that at the end of the selected school year, it will expire and a new verification will be required.
                  Selecting “Age Out” means that the verifications is valid until the student is no longer eligible for the program based on the allowed ages.`}
                        name={''}
                      />
                      <SelectWithLabel options={[{ id: 1, name: 'School Year' }, { id: 2, name: 'Age Out' }]} value={isdProgramData?.incomeVerificiationModeID ?? 0}
                        onChange={handleInputChange}
                        name={'incomeVerificiationModeID'} label='' width='100%'
                      />
                    </Box>
                  </Box>
                  <Box sx={{ pb: '20px' }}>
                    <SettingsGroup name='Income Calculation' icon={<AttachMoneyOutlinedIcon sx={{ width: '30px', height: '30px' }} />} showCheck
                      checked={isdProgramData?.requiresIncomeCalculation} checkDisabled
                    >
                      <>
                        {(isdProgramData?.requiresIncomeCalculation ?? false) && <>
                          <Box>
                            <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Grid item sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                <Box>FPL Income Threshold %</Box>
                              </Grid>
                              <Grid item sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                                <ProgramAgeSetting onChange={fplChanged} record={fplSettings} />
                              </Grid>

                            </Grid>
                          </Box>
                          <SettingDisplay name={'selfApproveOverIncome'} text={'Self-approve over income'} toolTip={'This setting will allow a service provider with the appropriate permissions to review and approve their own over income requests.'}
                            icon={<AssignmentTurnedInIcon />} checked={isdProgramData?.selfApproveOverIncome ?? false}
                            onChange={handleCheckChange}
                          />
                          {/*
                          <SettingDisplay name={'allowContinuousEnrollment'} text={'Allow Continuous Enrollment'} toolTip={undefined}
                            icon={<ContinuousEnrollmentIcon fill='black' />} checked={isdProgramData?.allowContinuousEnrollment ?? false}
                            onChange={handleCheckChange}
                          />
                     */}

                          <Box sx={{ pb: '20px' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <LabelWithToolTip labelText='Income Tracked Document' toolTipText='This document is how income is verified for an income verification.  Each document selected will have an associated income and it will be used to calculate a family’s Federal Poverty Level Percentage.'/>
                            </Box>
                            <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {fileTypes.filter(d => d.incomeRelated).map(f => {
                                return <DocumentDisplay fileType={f} docsList={selectedFileTypeIDs} onChange={handleChange} />
                              })}
                            </Grid>
                          </Box>

                          <SettingsGroup name='Prioritize Program' icon={<PriorityHighIcon sx={{ width: '30px', height: '30px' }} />}
                            showCheck={true} checkDisabled={(isdProgramData?.priorityProgram ?? false) && !(isdProgramData?.prioritizeOtherProgram ?? false)} checked={ (isdProgramData?.prioritizeOtherProgram ?? false)}
                            onCheckChange={prioProgramChange}
                          >
                            <>
                              {(isdProgramData?.prioritizeOtherProgram ?? false) && (<>
                                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                  <Box sx={{ width: 'auto' }}>
                                    <CustomLabelWithToolTip isdId={0} defaultLabelText='Program to Prioritize'
                                      defaultTooltipText={`The program selected here will function as a gatekeeper to any child enrolling in {Program name}.
                      {Program Name} income verifications must follow the income rules specified in the selected program below.
                      If it does not, a waiver will be required prior to verifying {Program Name}`}
                                      name={''}
                                    />
                                    <SelectWithLabel options={prioPrograms} value={isdProgramData?.prioritizedProgramID ?? -1} onChange={changePrioProgramID}
                                      name={''} label='' width='100%'
                                    />
                                  </Box>
                                  <Box>
                                    <Typography>Income Settings</Typography>
                                    <Grid container sx={{ display: 'flex', flexDirection: 'column' }}>
                                      <IncomeSettingDisplay val1='Age' val2='FPL%' fontWeight='bold' />
                                      {prioritizedProgram?.ageSettingList.map(a => {
                                        return <IncomeSettingDisplay val1={a.allowForAge.toString() ?? ''} val2={a.fplIncomeThreshhold?.toString() ?? ''} />
                                      })}
                                    </Grid>
                                  </Box>
                                </Box>
                              </>)}
                            </>
                          </SettingsGroup>
                        </>}

                      </>
                    </SettingsGroup>
                  </Box>

                  <SettingsGroup name='Document Requirements' icon={<FeedOutlinedIcon sx={{ width: '30px', height: '30px' }} />} showCheck={false}>
                    <Grid container sx={{ py: '10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {fileTypes.filter(d => !d.incomeRelated).map(f => {
                        return <DocumentDisplay fileType={f} docsList={selectedFileTypeIDs} onChange={handleChange} />
                      })}
                    </Grid>
                  </SettingsGroup>
                </>
              )}
            </>

          </SettingsGroup>
        </Box>
      </TabDisplay>
    </>
    return <Modal
      open={props.open}
      titleContent={titleContent}
      confirmationContent={content}
      onClose={handleClose}
      onConfirm={handleSubmit}
      buttonClassName='ActionButton'
      cancelButtonText='Close'
      confirmButtonText='Save Changes'
      confirmButtonColor='primary'
      maxWidth="sm"
      isUpdateDisabled={false}
    />
  }
}
